import { Def } from "@card/common/definitions";
import { Editor, Host } from "@card/common/messages";

import * as Constants from '@card/common/constants';
import { WindowMessenger } from "@card/common/window-messenger";

/**
 * Useful for Id generation
 * @param start first unique number (not used)
 * @param by increments unique number by this amount
 */
function createIncremNumberGenerator(start = 0, by = 1): () => number {
    // scope of last unique number is inside closure, so nothing else can access it
    let lastUniqueNumber = start
    return () => {
        lastUniqueNumber += by
        return lastUniqueNumber
    }
}

/** Apply config settings for debug and log stuff */
function applyConfig(config: Def.Config) {
    if (config.log) WindowMessenger.LOG = console.log.bind(console, '%cDEBUG HOST', 'font-weight: bold; color: blue')
    if (config.debug) WindowMessenger.DEBUG = console.log.bind(console, '%cDEBUG HOST', 'font-weight: bold; color: green')
}

class CreateEditorHost implements Def.CreateEditor {
    private pendingCardRequestsMap: Map<number, (response: Editor.Response, error: Def.CardError) => void> = new Map()

    // Messenger used to communicate with editor iframe
    private postMessage: (data: Host.Message) => void
    private receiveMessage: (listener: (data: Editor.Message) => void) => void

    // TODO update on load
    private expectedTargetHost: string = window.location.host

    constructor(private iframe: HTMLIFrameElement, private cardEditorHref: string) {
        if (!(iframe instanceof HTMLIFrameElement)) throw new TypeError("Expected iframe type instatiating CreateEditorHost")

        this.postMessage = WindowMessenger.createPoster(
            Constants.HOST_MESSAGE_PREFIX,
            () => iframe.contentWindow,
        )

        this.receiveMessage = WindowMessenger.createReceiver(
            Constants.EDITOR_MESSAGE_PREFIX
        )
    }

    private createUniqueId: () => number = createIncremNumberGenerator()

    private handleResponseMessage(message: Editor.Message) {
        if (message.type === "response") {

            console.log("responding to create guest card", message)
            const callback = this.pendingCardRequestsMap.get(message.request_id)
            if (typeof callback !== 'function')
                throw new ReferenceError(`Response could not be matched to callback with id (${message.request_id}) for message:\n${JSON.stringify(message, null, 2)}`)

            callback(message.response, message.error)
        }
    }

    // Be careful how you use this, the request type is not explicitly linked to the resolved type
    private createRequest<T extends Editor.Response>(request: Host.Request): Promise<T> {
        return new Promise<T>((resolve, reject) => {
            const requestId = this.createUniqueId()
            const callback = function requestCallback(response: Editor.Response, error?: Def.CardError) {
                if (error != null) reject(error)
                else resolve(response as T)
            }

            this.pendingCardRequestsMap.set(requestId, callback)

            this.postMessage({
                type: "request",
                id: requestId,
                request: request
            })
        })
    }

    private xsrfCookieValue(): string {
        try {
            return document.cookie
                .split('; ')
                .find(row => row.startsWith('XSRF-TOKEN='))
                .split('=')[1];
        } catch (e) {
            return '';
        }
    }

    private async createEditorWrapper(): Promise<Def.Editor> {
        return {
            createCard: (cardInputs) => {
                return this.createRequest({
                    type: "create_card",
                    data: cardInputs,
                    xsrf: this.xsrfCookieValue(),
                    token: sessionStorage.getItem('hoapay_accessToken')
                })
            },
            updateCard: (cardUpdate) => {
                return this.createRequest({
                    type: "update_card",
                    data: cardUpdate,
                    xsrf: this.xsrfCookieValue(),
                    token: sessionStorage.getItem('hoapay_accessToken')
                })
            },
            reset: () => {
                return this.createRequest({ type: "reset" })
            },
            touch: () => {
                return this.createRequest({ type: "touch" })
            },
        }
    }

    createNew(config: Def.Config, listeners: Def.ClientCardEditorListeners): Promise<Def.Editor> {
        applyConfig(config)

        this.receiveMessage((message) => {
            switch (message.type) {
                case "event":
                    const event = message.event
                    switch (event.type) {
                        case "touched": listeners.onTouched(event.touched); break;
                        case "validated": listeners.onValidated(event.is_valid, event.validation_errors); break;
                    }
                    break;

                case "response":
                    this.handleResponseMessage(message)
                    break;
            }
        })

        this.iframe.src = this.cardEditorHref
        this.iframe.addEventListener("load", () => {
            // coordinate with iframe.src set
            this.expectedTargetHost = window.location.host
            this.postMessage({ type: "create-new-card-editor", config: config })
        })

        return this.createEditorWrapper()
    }

    editExisting(config: Def.Config, lastFour: string, cardBrand: string): Promise<Def.Editor> {
        applyConfig(config)

        this.receiveMessage((message) => {
            switch (message.type) {
                case "event":
                    throw new TypeError(`Unexpected "event" message from existing editor, check to make sure old instances are being cleaned up.`)

                case "response":
                    this.handleResponseMessage(message)
                    break;
            }
        })

        this.iframe.src = this.cardEditorHref
        this.iframe.addEventListener("load", () => {
            // coordinate with iframe.src set
            this.expectedTargetHost = window.location.host
            this.postMessage({ type: "create-existing-card-editor", config: config, card_brand: cardBrand, last_four: lastFour })
        })


        return this.createEditorWrapper()
    }
}

export function setupEditorHost(iframe: HTMLIFrameElement, cardEditorHref: string): Def.CreateEditor {
    return new CreateEditorHost(iframe, cardEditorHref)
}

